import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { FormGroup } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  employeeChangePasswordThunk,
  clearEmployeeChangePasswordError,
  clearEmployeeChangePasswordSuccess,
} from "../../../../ReduxToolkit/Slice/ChangePasswordSlice";
import CommonLogo from "../Common/CommonLogo";
import { Btn, H4, P } from "../../../../AbstractElements";
import {
  CreateYourPassword,
  NewPassword,
  RetypePassword,
  SubmitButton,
} from "../../../../utils/Constant";
import { ShowSuccessAlert } from "../../../BonusUi/SweetAlert/ShowSuccessAlert";
import { ShowDangerAlert } from "../../../BonusUi/SweetAlert/ShowDangerAlert/ShowDangerAlert";
import { useAlerts } from "../../../../hooks/useAlert";
import { InputField } from "../../../Forms/FormsControl/CustomInput/CustomInput";
import { resetPasswordValidationSchema } from "../../../../features/employeeManagement/validationSchema";

type Props = {
  userId: number;
  email: string;
};

const ChangePasswordForm = ({ userId, email }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  const {
    data: passwordChangeResponse,
    error: passwordChangeError,
    loading,
  } = useAppSelector(({ employeeChangePassword }) => employeeChangePassword);

  const handleSubmit = async (values: {
    password: string;
    password_confirmation: string;
  }) => {
    await dispatch(
      employeeChangePasswordThunk({
        id: userId,
        passwordPayload: {
          password: values.password,
          password_confirmation: values.password_confirmation,
        },
      })
    );
  };

  const alertConfig = [
    {
      textMessage: passwordChangeError,
      alertFunc: ShowDangerAlert,
      clearActions: [clearEmployeeChangePasswordError],
    },
  ];
  useAlerts(alertConfig);

  useEffect(() => {
    if (passwordChangeResponse?.success) {
      ShowSuccessAlert({
        text: passwordChangeResponse.message || "Password changed successfully",
        onConfirm: () => {
          dispatch(clearEmployeeChangePasswordSuccess());
          navigate("/login");
        },
      });
    }
  }, [passwordChangeResponse]);

  return (
    <div className="login-card login-dark">
      <div>
        <div className="login-main">
          <div>
            <CommonLogo alignLogo="d-flex justify-content-center align-items-center" />
          </div>

          <Formik
            initialValues={{ password: "", password_confirmation: "" }}
            onSubmit={handleSubmit}
            validationSchema={resetPasswordValidationSchema}
          >
            {({ errors, touched }) => {
              return (
                <Form className="theme-form">
                  <H4>{CreateYourPassword}</H4>

                  {/* New Password */}
                  <FormGroup>
                    <div className="form-input position-relative">
                      <InputField
                        md="12"
                        label={NewPassword}
                        name="password"
                        type={showPassword ? "text" : "password"}
                        setShowPassword={setShowPassword}
                        errors={errors}
                        touched={touched}
                        placeholder="*********"
                      />
                    </div>
                  </FormGroup>

                  {/* Confirm Password */}
                  <FormGroup>
                    <div className="form-input position-relative">
                      <InputField
                        md="12"
                        label={RetypePassword}
                        name="password_confirmation"
                        type={showConfirm ? "text" : "password"}
                        errors={errors}
                        touched={touched}
                        placeholder="*********"
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-0">
                    <Btn
                      color="primary"
                      block
                      className="w-100"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? "Processing..." : SubmitButton}
                    </Btn>
                  </FormGroup>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordForm;
